import NotFound from '../NotFound';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCaseStudyBySlug, resources } from './data';
import SEO, { articleJsonLd, breadcrumbJsonLd, faqJsonLd, SITE_URL } from '../../components/SEO';

/** Map a case study's industry label to an existing /industries/:slug page (or null). */
function industrySlugFor(industry: string): string | null {
  const c = industry.toLowerCase();
  if (c.includes('restaurant') || c.includes('qsr')) return 'restaurants';
  if (c.includes('pharmac') || c.includes('healthcare')) return 'pharmacies';
  if (c.includes('grocery') || c.includes('q-commerce')) return 'grocery';
  if (c.includes('3pl') || c.includes('contract logistics')) return 'contract-logistics';
  if (c.includes('e-commerce') || c.includes('ecommerce') || c.includes('retail')) return 'ecommerce';
  if (c.includes('courier') || c.includes('parcel') || c.includes('express') || c.includes('logistics'))
    return 'couriers';
  return null;
}
import type {
  CaseStudyNarrativeSection,
  CaseStudyInfographic,
  CaseStudyAnalytics,
  CaseStudyImage,
  CaseStudyTimelinePhase,
} from './ResourcePage';

function NarrativeSection({ section }: { section: CaseStudyNarrativeSection }) {
  return (
    <div className="mb-8">
      {section.heading && (
        <h2 className="text-xl font-bold text-gray-900 mb-4 scroll-mt-24">{section.heading}</h2>
      )}
      <div className="space-y-4">
        {section.paragraphs.map((p, i) => (
          <p key={i} className="text-gray-700 leading-relaxed">
            {p}
          </p>
        ))}
      </div>
    </div>
  );
}

function InfographicBlock({ item }: { item: CaseStudyInfographic }) {
  return (
    <figure className="my-10">
      <div className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50 shadow-sm">
        <img
          src={item.image}
          alt={item.alt}
          className="w-full h-auto object-contain max-h-[480px] mx-auto"
        />
      </div>
      {(item.title || item.caption) && (
        <figcaption className="mt-3 text-center">
          {item.title && (
            <span className="block text-sm font-semibold text-gray-900 mb-1">{item.title}</span>
          )}
          {item.caption && <span className="text-sm text-gray-500">{item.caption}</span>}
        </figcaption>
      )}
    </figure>
  );
}

function AnalyticsBlock({ block }: { block: CaseStudyAnalytics }) {
  const { t } = useTranslation();
  return (
    <div className="my-10 p-6 lg:p-8 bg-gray-50 rounded-2xl border border-gray-100">
      <h3 className="text-lg font-bold text-gray-900 mb-1">{block.title}</h3>
      {block.subtitle && (
        <p className="text-sm text-gray-500 mb-6">{block.subtitle}</p>
      )}
      {block.beforeAfter && block.beforeAfter.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 font-semibold text-gray-700">{t('caseStudies.detail.analytics.metric')}</th>
                <th className="text-right py-3 font-semibold text-gray-700">{t('caseStudies.detail.analytics.before')}</th>
                <th className="text-right py-3 font-semibold text-blue-600">{t('caseStudies.detail.analytics.after')}</th>
              </tr>
            </thead>
            <tbody>
              {block.beforeAfter.map((row, i) => (
                <tr key={i} className="border-b border-gray-100">
                  <td className="py-3 text-gray-700">{row.label}</td>
                  <td className="py-3 text-right text-gray-500">{row.before}</td>
                  <td className="py-3 text-right font-semibold text-blue-600">{row.after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {block.stats && block.stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
          {block.stats.map((stat, i) => (
            <div key={i} className="text-center p-3 bg-white rounded-xl border border-gray-100">
              <div className="text-2xl font-bold text-blue-600">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      )}
      {block.chartLabel && (
        <p className="text-xs text-gray-400 mt-4">{block.chartLabel}</p>
      )}
    </div>
  );
}

function ImageBlock({ item }: { item: CaseStudyImage }) {
  return (
    <figure className="my-8">
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <img src={item.src} alt={item.alt} className="w-full h-auto object-cover" />
      </div>
      {item.caption && (
        <figcaption className="text-sm text-gray-500 mt-2 text-center">{item.caption}</figcaption>
      )}
    </figure>
  );
}

function TimelineBlock({ phases }: { phases: CaseStudyTimelinePhase[] }) {
  const { t } = useTranslation();
  return (
    <div className="my-10">
      <h3 className="text-lg font-bold text-gray-900 mb-6">{t('caseStudies.detail.timelineTitle')}</h3>
      <div className="space-y-4">
        {phases.map((phase, i) => (
          <div
            key={i}
            className="flex gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100"
          >
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
              {i + 1}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="font-semibold text-gray-900">{phase.phase}</span>
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                  {phase.duration}
                </span>
              </div>
              <p className="text-sm text-gray-600">{phase.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CaseStudyDetailPage() {
  const { t } = useTranslation();
  const { caseSlug } = useParams<{ caseSlug: string }>();
  const study = caseSlug ? getCaseStudyBySlug(caseSlug) : undefined;

  if (!study) {
    return <NotFound />;
  }

  const content = study.fullContent;
  const allStudies = resources['case-studies']?.caseStudies ?? [];
  /** Related case studies: same industry first, then others; top 3. */
  const relatedStudies = allStudies
    .filter((s) => s.slug !== study.slug)
    .map((s) => ({ s, sameIndustry: s.industry === study.industry ? 1 : 0 }))
    .sort((a, b) => b.sameIndustry - a.sameIndustry)
    .slice(0, 3)
    .map((x) => x.s);
  const industrySlug = industrySlugFor(study.industry);
  const hasFullContent = content && (
    content.narrative?.length > 0 ||
    content.infographics?.length > 0 ||
    content.analytics?.length > 0 ||
    content.images?.length > 0 ||
    content.keyTakeaways?.length > 0 ||
    (content.timeline && content.timeline.length > 0)
  );

  return (
    <div className="pt-24">
      <SEO
        title={study.seo?.documentTitle ?? study.headline}
        description={study.seo?.metaDescription ?? study.challenge}
        exactDocumentTitle={!!study.seo}
        ogImage={study.heroImage ? `${SITE_URL}${study.heroImage}` : undefined}
        ogType="article"
        jsonLd={[
          articleJsonLd({
            type: 'Article',
            headline: study.headline,
            description: study.challenge,
            url: `${SITE_URL}/resources/case-studies/${caseSlug}`,
            image: study.heroImage ? `${SITE_URL}${study.heroImage}` : undefined,
            section: t('caseStudies.detail.schemaSection'),
          }),
          breadcrumbJsonLd([
            { name: t('common.breadcrumb.home'), url: 'https://geofleet.ai' },
            { name: t('common.breadcrumb.resources'), url: 'https://geofleet.ai/resources' },
            { name: t('common.breadcrumb.caseStudies'), url: 'https://geofleet.ai/resources/case-studies' },
            { name: study.headline, url: `${SITE_URL}/resources/case-studies/${caseSlug}` },
          ]),
          ...(study.faq && study.faq.length > 0 ? [faqJsonLd(study.faq)] : []),
        ]}
      />
      {/* Hero */}
      <header className="relative w-full aspect-[21/9] min-h-[280px] max-h-[440px] overflow-hidden bg-gray-900">
        {study.heroImage && (
          <img
            src={study.heroImage}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/resources/case-studies"
              className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-4"
            >
              <i className="ri-arrow-left-line" />
              {t('caseStudies.detail.backLink')}
            </Link>
            <span className="inline-block text-[10px] font-bold uppercase tracking-wider text-white/90 bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded mb-3">
              {study.industry}
            </span>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-sm">
              {study.headline}
            </h1>
            <p className="text-sm text-white/80 mt-2">{study.company}</p>
            {study.tags && study.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {study.tags.map((tag, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-medium text-white/90 bg-white/15 backdrop-blur-sm px-2.5 py-1 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Summary + metrics strip */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            {study.metrics.map((m, i) => (
              <div key={i} className="text-center p-4 rounded-xl bg-blue-50/50 border border-blue-100/50">
                <div className="text-2xl font-bold text-blue-600">{m.value}</div>
                <div className="text-xs text-gray-600 mt-0.5">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">
                {t('caseStudies.detail.challengeHeading')}
              </h2>
              <p className="text-gray-700 leading-relaxed">{study.challenge}</p>
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">
                {t('caseStudies.detail.solutionHeading')}
              </h2>
              <p className="text-gray-700 leading-relaxed">{study.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Full content */}
      {hasFullContent && content && (
        <section className="py-12 lg:py-16 bg-gray-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-8 lg:p-10 shadow-sm">
              {content.narrative?.map((section, i) => (
                <NarrativeSection key={i} section={section} />
              ))}
              {content.infographics?.map((item, i) => (
                <InfographicBlock key={i} item={item} />
              ))}
              {content.analytics?.map((block, i) => (
                <AnalyticsBlock key={i} block={block} />
              ))}
              {content.images?.map((item, i) => (
                <ImageBlock key={i} item={item} />
              ))}
              {content.timeline && content.timeline.length > 0 && (
                <TimelineBlock phases={content.timeline} />
              )}
              {content.keyTakeaways && content.keyTakeaways.length > 0 && (
                <div className="my-10 p-6 bg-blue-50/50 rounded-2xl border border-blue-100/50">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">{t('caseStudies.detail.keyTakeaways')}</h3>
                  <ul className="space-y-2">
                    {content.keyTakeaways.map((takeaway, i) => (
                      <li key={i} className="flex gap-2 text-gray-700">
                        <i className="ri-checkbox-circle-fill text-blue-500 flex-shrink-0 mt-0.5" />
                        <span>{takeaway}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {study.faq && study.faq.length > 0 && (
        <section className="py-12 lg:py-14 bg-white border-t border-gray-100">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('common.faqTitle')}</h2>
            <div className="space-y-3">
              {study.faq.map((item, i) => (
                <details key={i} className="group rounded-2xl border border-gray-200 bg-gray-50/80">
                  <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none text-base font-semibold text-gray-900">
                    <span>{item.question}</span>
                    <i className="ri-add-line text-gray-400 group-open:hidden shrink-0" aria-hidden />
                    <i className="ri-subtract-line text-blue-600 hidden group-open:block shrink-0" aria-hidden />
                  </summary>
                  <div className="px-5 pb-5 -mt-1">
                    <p className="text-sm text-gray-700 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quote */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6">
          <blockquote className="border-l-4 border-blue-500 pl-6 py-2 bg-gray-50 rounded-r-xl">
            <p className="text-lg text-gray-800 italic mb-4">"{study.quote}"</p>
            <footer className="text-sm font-semibold text-gray-900">{study.author}</footer>
            <p className="text-xs text-gray-500">{study.role} · {study.industry}</p>
          </blockquote>
        </div>
      </section>

      {/* Industry cross-link */}
      {industrySlug && (
        <section className="py-10 bg-white border-t border-gray-100">
          <div className="max-w-3xl mx-auto px-6">
            <Link
              to={`/industries/${industrySlug}`}
              className="group flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-gray-50/80 px-6 py-5 hover:border-blue-200 hover:bg-blue-50/50 transition-all"
            >
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-blue-600 mb-1">{t('caseStudies.detail.exploreSolution.badge')}</p>
                <p className="text-base font-semibold text-gray-900">{t('caseStudies.detail.exploreSolution.title', { industry: study.industry })}</p>
              </div>
              <i className="ri-arrow-right-line text-xl text-blue-600 group-hover:translate-x-1 transition-transform" aria-hidden />
            </Link>
          </div>
        </section>
      )}

      {/* Related case studies */}
      {relatedStudies.length > 0 && (
        <section className="py-14 lg:py-16 bg-[#FAFAFA] border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 lg:mb-10">{t('caseStudies.detail.relatedTitle')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {relatedStudies.map((rs) => (
                <Link
                  key={rs.slug}
                  to={`/resources/case-studies/${rs.slug}`}
                  className="group flex flex-col bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-blue-200 hover:shadow-lg transition-all duration-200"
                >
                  {rs.heroImage && (
                    <div className="aspect-[16/10] overflow-hidden bg-gray-100">
                      <img src={rs.heroImage} alt="" className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                    </div>
                  )}
                  <div className="flex flex-col flex-1 p-5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-blue-600 mb-2">{rs.industry}</span>
                    <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-2 leading-snug">
                      {rs.headline}
                    </h3>
                    <div className="flex flex-wrap gap-4 mt-auto pt-2">
                      {rs.metrics.slice(0, 3).map((m, j) => (
                        <div key={j}>
                          <div className="text-sm font-bold text-blue-600">{m.value}</div>
                          <div className="text-[10px] text-gray-500">{m.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-blue-600">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">{t('caseStudies.detail.cta.title')}</h2>
          <p className="text-base text-white/80 mb-7">{t('caseStudies.detail.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white px-6 py-2.5 rounded-lg text-sm font-semibold text-blue-600 hover:bg-gray-100 transition-all shadow-md"
            >
              {t('common.ctaStartTrial')}
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white/80 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-all"
            >
              {t('common.ctaBookDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
